import React, { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import {
  FocusContext,
  useFocusable,
} from '@noriginmedia/norigin-spatial-navigation'
import { truncate, addMore } from '../../../../shared/helpers'
import tts from '../../lib/TTS'
import type { RootState } from '../../reducers/types/reducerTypes'
import { useAppSelector } from '../../hooks/useStore'
import ActionButtons from './ActionButtons'
import Description from '../FocusableLink'
import FadedImage from '../FadedImage'
import Logo from '../Logo'
import ProgressBar from '../ProgressBar'
import TextModal from '../TextModal'
import type { ContinueWatchingProps } from './types'

const CONTINUE_WATCHING_KEY = 'CONTINUE_WATCHING_KEY'
const DESCRIPTION_TEXT_KEY = 'DESCRIPTION_TEXT_KEY'

function ContinueWatching({
  onArrowPress,
  onFocus,
  onDescriptionModalChange,
}: ContinueWatchingProps) {
  const [descriptionModalVisible, setDescriptionModalVisible] = useState(false)
  const showDetail = useAppSelector((state: RootState) => state.showDetail)
  const { continueWatchingData, pageDetails } = showDetail
  const {
    logo,
    mezzanineImage,
    description_long: showDescription,
    title,
  } = pageDetails
  const {
    contentSecondsWatched,
    contentDescription,
    contentDuration,
    contentEpisode,
    contentHasSecondsWatched,
    contentSeason,
    contentTitle,
  } = continueWatchingData
  const episode = contentEpisode ? `E${contentEpisode}: ` : ''
  const season = contentSeason ? `S${contentSeason}` : ''
  const { ref, focusKey } = useFocusable({
    focusKey: CONTINUE_WATCHING_KEY,
    onFocus,
  })

  useEffect(() => {
    if (title) {
      tts.pushTextToRead(`...${title}`)
    }
    if (contentSeason && contentEpisode && contentTitle) {
      tts.enqueueTextToRead(
        `...... Season ${contentSeason} Episode ${contentEpisode}: ${contentTitle}${
          contentDescription ? ` .... ${contentDescription}` : ''
        }`
      )
    } else if (contentDescription) {
      tts.enqueueTextToRead(`...${contentDescription}...`)
    }
  }, [title, contentSeason, contentEpisode, contentTitle, contentDescription])

  const descriptionLabel = `${truncate.apply(showDescription, [
    100,
    true,
  ])} ... Press enter to read more`

  const handleDescriptionModal = () => {
    const newVisible = !descriptionModalVisible

    setDescriptionModalVisible(newVisible)
    onDescriptionModalChange?.(newVisible)

    if (descriptionModalVisible) {
      tts.addTextToRead(descriptionLabel)
    }
  }

  return (
    <FocusContext.Provider value={focusKey}>
      <div ref={ref} className="continue-watching">
        {descriptionModalVisible &&
          document.getElementById('app') &&
          createPortal(
            <TextModal
              title={title}
              content={showDescription}
              handleDescriptionModal={handleDescriptionModal}
            />,
            document.getElementById('app')!
          )}
        <div className="continue-watching__information">
          <Logo
            className="continue-watching__information"
            hasLogo={!!logo}
            logoToDisplay={logo || title}
          />
          <Description
            className="continue-watching__information-description"
            focusKey={DESCRIPTION_TEXT_KEY}
            label={descriptionLabel}
            onEnterPress={handleDescriptionModal}
          >
            <span className="continue-watching__information-description-text">
              {truncate.apply(showDescription, [100, true])}
            </span>
            <span className="continue-watching__information-description-more-text">
              {addMore.apply(showDescription, [100])}
            </span>
          </Description>
          <div className="continue-watching__information-buttons">
            <ActionButtons onArrowPress={onArrowPress} />
          </div>
        </div>
        <div className="continue-watching__content">
          <FadedImage
            className="continue-watching__content"
            imageSource={mezzanineImage}
          />
          <div className="continue-watching__content-video">
            <div className="continue-watching__content-video-details">
              <span className="continue-watching__content-video-details-title">{`${season} ${episode} ${contentTitle}`}</span>
              <span className="continue-watching__content-video-details-short-description">
                {contentDescription}
              </span>
            </div>
            {contentHasSecondsWatched &&
              contentSecondsWatched != null &&
              contentDuration != null &&
              !descriptionModalVisible && (
                <div className="continue-watching__content-video-progress-bar">
                  <ProgressBar
                    current={contentSecondsWatched}
                    total={contentDuration}
                    fullWidth
                  />
                </div>
              )}
          </div>
        </div>
      </div>
    </FocusContext.Provider>
  )
}

export default ContinueWatching
